import { useEffect, useState } from 'react'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

export default function Animation37({ animation, badge, all_animations }: Props) {
  const [ribbonPhase, setRibbonPhase] = useState(0)
  const [showBadge, setShowBadge] = useState(false)

  useEffect(() => {
    setTimeout(() => setRibbonPhase(1), 300)
    setTimeout(() => setRibbonPhase(2), 800)
    setTimeout(() => setRibbonPhase(3), 1300)
    setTimeout(() => {
      setRibbonPhase(4)
      setShowBadge(true)
    }, 1800)
  }, [])

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes ribbonUnwrap {
          0% {
            transform: scaleX(0);
          }
          100% {
            transform: scaleX(1);
          }
        }
        @keyframes ribbonFall {
          0% {
            transform: translateY(0) rotate(0);
            opacity: 1;
          }
          100% {
            transform: translateY(100px) rotate(45deg);
            opacity: 0;
          }
        }
        @keyframes badgeUnwrap {
          0% {
            transform: scale(0.5);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        .ribbon-unwrap {
          animation: ribbonUnwrap 0.4s ease-out forwards;
          transform-origin: left;
        }
        .ribbon-fall {
          animation: ribbonFall 0.8s ease-in forwards;
        }
        .badge-unwrap {
          animation: badgeUnwrap 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh]">
        <div className="relative">
          {/* Rubans qui se déroulent */}
          {ribbonPhase >= 1 && ribbonPhase < 4 && (
            <>
              <div
                className={`absolute h-8 bg-tessan-yellow rounded-full ${ribbonPhase >= 3 ? 'ribbon-fall' : 'ribbon-unwrap'}`}
                style={{
                  width: '200px',
                  left: '-100px',
                  top: '30%'
                }}
              />
              <div
                className={`absolute h-8 bg-tessan-yellow/70 rounded-full ${ribbonPhase >= 3 ? 'ribbon-fall' : 'ribbon-unwrap'}`}
                style={{
                  width: '180px',
                  right: '-90px',
                  top: '50%',
                  animationDelay: '0.2s'
                }}
              />
              <div
                className={`absolute h-6 bg-tessan-yellow/50 rounded-full ${ribbonPhase >= 3 ? 'ribbon-fall' : 'ribbon-unwrap'}`}
                style={{
                  width: '160px',
                  left: '-80px',
                  top: '70%',
                  animationDelay: '0.4s'
                }}
              />
            </>
          )}

          {/* Nœud central */}
          {ribbonPhase >= 1 && ribbonPhase < 4 && (
            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
              <div className={`w-16 h-16 bg-tessan-yellow rounded-full flex items-center justify-center shadow-lg ${ribbonPhase >= 3 ? 'ribbon-fall' : ''}`}>
                <span className="text-2xl">🎀</span>
              </div>
            </div>
          )}

          {/* Boîte emballée */}
          {ribbonPhase < 4 && (
            <div className="w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream/30 rounded-2xl border-4 border-dashed border-tessan-cream/50" />
          )}

          {/* Badge révélé */}
          {showBadge && (
            <div className="badge-unwrap">
              <div className="w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream rounded-2xl flex items-center justify-center shadow-2xl shadow-tessan-yellow/50">
                <span className="text-6xl sm:text-8xl">{badge.emoji}</span>
              </div>
            </div>
          )}
        </div>

        <div className={`mt-8 text-center transition-all duration-700 ${showBadge ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="text-2xl sm:text-4xl font-black text-tessan-yellow">{badge.name}</div>
          <div className="text-lg sm:text-xl text-tessan-cream mt-2">+{badge.points_awarded} points</div>
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs">{badge.description}</div>
        </div>
      </div>
    </AnimationLayout>
  )
}
